import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TicketModel } from '../../../../modules/invoice/tickets/ticket.model';
import { AccountsAndTaxesModel } from '../../../../modules/invoice/preinvoice/accountsandtaxes.model';
import { ResultModel } from '../../../../shared/result.model';
import { TicketFilterModel } from '../../../../modules/invoice/tickets/ticketFilter.model';
import {
  VerifactuEventModel,
  VerifactuQRModel,
} from '../../../../modules/invoice/preinvoice/verifactu-qr.model';
import { environment } from '../../../../../environments/environment';

const LIST_ENDPOINT = '/tickets';

@Injectable({
  providedIn: 'root',
})
export class OldTicketService {
  private readonly http = inject(HttpClient);
  ticketList: TicketModel[] = [];

  private readonly saveRequestSubject = new BehaviorSubject<boolean>(false);
  public readonly saveRequestObservable$: Observable<boolean> =
    this.saveRequestSubject.asObservable();

  public saveRequestUpdateValue(value: boolean): void {
    this.saveRequestSubject.next(value);
  }

  public async pdfMailTicket(uid: string, email: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<{ message: string }>(
        environment.apiUrl + '/pdfmail' + `/${uid}` + `/true` + `/${email}`
      )
    );

    return response.message;
  }

  public async pdf(uid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + '/pdfticket' + `/${uid}`, {
        responseType: 'blob',
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  public async pdfRegalo(uid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + '/pdfticketregalo' + `/${uid}`, {
        responseType: 'blob',
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  public async accountsAndTaxesData(): Promise<AccountsAndTaxesModel> {
    const response = await firstValueFrom(
      this.http.get<{ data: AccountsAndTaxesModel }>(environment.apiUrl + '/accounting')
    );

    return response.data;
  }

  public async addData(ticket: TicketModel): Promise<TicketModel> {
    const response = await firstValueFrom(
      this.http.post<{ data: TicketModel }>(environment.apiUrl + LIST_ENDPOINT, ticket)
    );

    return response.data;
  }

  public async deleteData(uid: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.delete<{ Message: string }>(environment.apiUrl + LIST_ENDPOINT + `/${uid}`)
    );

    return response.Message;
  }

  public async editData(uid: string, ticket: TicketModel): Promise<TicketModel> {
    const response = await firstValueFrom(
      this.http.put<{ data: TicketModel }>(environment.apiUrl + LIST_ENDPOINT + `/${uid}`, ticket)
    );

    return response.data;
  }

  public async loadData(uid: string): Promise<TicketModel> {
    const response = await firstValueFrom(
      this.http.get<{ data: TicketModel }>(environment.apiUrl + LIST_ENDPOINT + `/edit/${uid}`)
    );

    return response.data;
  }

  public async getList(offset?: number, limit?: number): Promise<ResultModel<TicketModel>> {
    let params = new HttpParams();

    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<{ data: ResultModel<TicketModel> }>(environment.apiUrl + LIST_ENDPOINT, {
        params,
      })
    );

    return response.data;
  }

  public async export(uids: string[]): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/export', uids, {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  public async getListFilter(
    activityUid: string,
    model: TicketFilterModel
  ): Promise<TicketModel[]> {
    const response = await firstValueFrom(
      this.http.post<{ data: TicketModel[] }>(
        environment.apiUrl + LIST_ENDPOINT + `/${activityUid}`,
        model
      )
    );

    return response.data;
  }

  public async chargeTotalValue(ticketUid: string): Promise<TicketModel> {
    const response = await firstValueFrom(
      this.http.get<{ data: TicketModel }>(
        environment.apiUrl + LIST_ENDPOINT + `/ChargeTotalValue/${ticketUid}`
      )
    );

    return response.data;
  }

  public async rectify(ticket: TicketModel): Promise<TicketModel> {
    const response = await firstValueFrom(
      this.http.put<{ data: TicketModel }>(
        environment.apiUrl + LIST_ENDPOINT + `/rectify`,
        ticket
      )
    );

    return response.data;
  }

  public async pdfdummy(): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + '/pdfticket/pdfdummy', {
        responseType: 'blob',
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  public async getQR(uid: string): Promise<VerifactuQRModel> {
    return await firstValueFrom(
      this.http.get<VerifactuQRModel>(`${environment.apiUrl}${LIST_ENDPOINT}/${uid}/verifactu/qr`)
    );
  }

  public async getEvents(uid: string): Promise<VerifactuEventModel> {
    return await firstValueFrom(
      this.http.get<VerifactuEventModel>(
        `${environment.apiUrl}${LIST_ENDPOINT}/${uid}/verifactu/events`
      )
    );
  }

  public async sendByEmail(uid: string, email: string): Promise<void> {
    const body = { email };
    await firstValueFrom(this.http.post<void>(`/api/tickets/${uid}/email`, body));
  }
}